import {
  calendarClock,
  chartMixedUpCircleDollar,
  circleDollar,
} from "@simplybusiness/icons";
import { Flex, Icon, Text, useBreakpoint } from "@simplybusiness/mobius";
import { useAtom } from "jotai";
import { paymentToggleAtom } from "../../../../store";
import { QuoteCardBadge, QuoteCardBadges } from "../QuoteCardBadges";
import { Premium } from "./Premium";
import type { PricingProps } from "./types";
import { OfflinePurchaseText } from "./OfflinePurchaseText";
import { useExperiment } from "../../../../hooks/experiments";

export const Pricing = ({
  quote,
  displayVariant,
  displayBadges,
  showInstallmentsInformation,
}: PricingProps) => {
  const {
    installmentsAvailable,
    downpaymentAmount,
    installmentsAmount,
    installmentsCount,
    installmentsTotal,
    premium,
    externalPaymentOptions,
  } = quote;
  const [paymentPeriod] = useAtom(paymentToggleAtom);
  const isMonthly = paymentPeriod === "monthly";
  const hasInstallments = installmentsAvailable && installmentsAmount;
  const showZeroInterestBadge =
    hasInstallments &&
    isMonthly &&
    !externalPaymentOptions &&
    !quote.offlinePurchaseOnly;
  const premiumEqualsInstallments = installmentsTotal === premium;
  const showTotalCostBadge =
    hasInstallments && !isMonthly && premiumEqualsInstallments;
  const { processingFee } = quote;
  const amountDueClasses = "mobius-quote-card__pricing-amount-due";
  const showInstalments =
    isMonthly && hasInstallments && showInstallmentsInformation;
  const { down } = useBreakpoint();
  const isMobile = down("md");
  const displayOfflinePurchaseText =
    displayVariant != "qds" &&
    quote.offlinePurchaseOnly &&
    quote.offlinePurchaseOnlyRfq;
  const isCancelAnytime =
    useExperiment("us2602QcpCancelAnytime") === "treatment";

  let installmentPremiumToShow;
  if (externalPaymentOptions && installmentsTotal) {
    installmentPremiumToShow = installmentsTotal;
  } else if (isMonthly && installmentsTotal) {
    installmentPremiumToShow = installmentsTotal;
  } else {
    installmentPremiumToShow = premium;
  }
  return (
    <Flex flexDirection="column" className="mobius-quote-card__pricing">
      <Flex flexDirection="column">
        <>
          <Flex alignItems="baseline" gap="xxs">
            <Premium
              isMonthly={isMonthly}
              hasInstallments={hasInstallments}
              installmentsAmount={installmentsAmount}
              premium={premium!}
              offlinePurchaseOnly={quote.offlinePurchaseOnly}
            />
          </Flex>
          {showInstalments && (
            <>
              {!quote.offlinePurchaseOnly && (
                <>
                  <Text variant="small" className={amountDueClasses}>
                    Due today {downpaymentAmount}
                  </Text>

                  <Text variant="small" className={amountDueClasses}>
                    Then {installmentsCount} monthly installments of{" "}
                    {installmentsAmount}
                  </Text>
                </>
              )}
              <Flex gap="xxs" className="mobius-quote-card__pricing-total">
                <Text
                  data-testid="pricing__pricing-total-amount"
                  className="mobius-quote-card__pricing-total-amount"
                  elementType="span"
                  variant="small"
                >
                  Total {installmentPremiumToShow}
                  {quote.offlinePurchaseOnly && " (estimated)"}
                </Text>
              </Flex>
            </>
          )}
          {processingFee && (
            <Flex gap="xxs" className="mobius-quote-card__pricing-total">
              <Text
                data-testid="pricing__processing-fees"
                elementType="span"
                variant="small"
              >
                {`Inc. ${processingFee} processing fee`}
              </Text>
            </Flex>
          )}
        </>
        {displayOfflinePurchaseText && (
          <OfflinePurchaseText
            offlinePurchaseOnlyText={quote.offlinePurchaseOnlyText}
            offlinePurchaseOnlyNumber={quote.offlinePurchaseOnlyNumber}
            isMobile={isMobile}
          />
        )}
        {displayBadges && !isCancelAnytime && (
          <QuoteCardBadges>
            <QuoteCardBadge
              icon={
                <Icon
                  icon={calendarClock}
                  title="Policy duration information"
                />
              }
              data-testid="quote-card-badge-12-month-policy"
            >
              12 month policy
            </QuoteCardBadge>
            {showZeroInterestBadge && (
              <QuoteCardBadge
                icon={
                  <Icon
                    icon={chartMixedUpCircleDollar}
                    title="Zero interest payment option"
                  />
                }
                data-testid="quote-card-badge-zero-interest"
              >
                Zero interest
              </QuoteCardBadge>
            )}
            {showTotalCostBadge && (
              <QuoteCardBadge
                icon={
                  <Icon
                    icon={circleDollar}
                    title="Cost comparison information"
                  />
                }
                data-testid="quote-card-badge-annual-cost-same-as-monthly"
              >
                Total annual cost same as monthly
              </QuoteCardBadge>
            )}
          </QuoteCardBadges>
        )}
      </Flex>
    </Flex>
  );
};
